import { TargetViewers, ProfileTypes } from '@/constants/profileType.constant';
import { Popover, Transition } from '@headlessui/react';
import Typography from '@ui/Typography';
import { cx } from 'cva';
import { last } from 'lodash-es';
import React, { Fragment, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { BiCaretDown, BiWorld } from 'react-icons/bi';
import { RiAdminFill, RiUserFill } from 'react-icons/ri';
import { GoHomeFill } from 'react-icons/go';
import { MdVpnLock } from 'react-icons/md';
import useStore from '../../stores/store';
import ProfileAvatar from '../ProfileSelector/ProfileAvatar';
import CompanySearchBox from '../next-page-components/CompanySearchBox';
import NonProfitSearchBox from '../next-page-components/NonProfitSearchBox';

function PostViewersSelectorNekiAdmin(props) {
  const {
    post = null,
    disabled = false,
    setPostPayload = null,
    isP2PFundraiser = false,
  } = props;

  const user = useStore((state) => state.user);
  const isNonProfitAdmin = user.isNonProfitAdmin;
  const selectedProfile = user.profile.selectedProfile;
  const displayName = user.profile.displayName;
  const userProfile = user.profile;
  const isNekiAdmin = user.isNekiAdmin;

  const [authorTypes, setAuthorTypes] = useState([
    {
      value: 'org',
      title: userProfile.activeCommunity.name,
      subtitle: 'Admin',
      icon: RiAdminFill,
    },
    {
      value: 'me',
      title: userProfile.fullName,
      subtitle: 'User',
      icon: RiUserFill,
    },
  ]);
  const [targetAudiences, setTargetAudiences] = useState([
    {
      value: 'all',
      title: 'Everyone',
      subtitle: 'All my supporters',
      icon: BiWorld,
    },
    {
      value: 'selected-org',
      title: 'Selected Communities',
      subtitle: 'Select communities to target',
      icon: MdVpnLock,
    },
    {
      value: 'my-org',
      title: userProfile.activeCommunity.name,
      subtitle: 'Everyone in my community',
      icon: GoHomeFill,
    },
  ]);

  const {
    getValues,
    setValue,
    formState: { errors },
    trigger,
    control,
    watch,
  } = useFormContext();

  const getDefaultAudience = () => {
    const selected = getValues('targetViewers');
    let audience = targetAudiences.find((a) => a.value === selected);

    // TODO: keep the target audience same while editing
    if (disabled && post) {
      audience = targetAudiences.find((a) => a.value === post?.targetViewers);
    }

    if (audience) {
      return audience;
    }

    if (isP2PFundraiser) {
      setValue('targetViewers', TargetViewers.All);
      return targetAudiences[0];
    }

    return isNonProfitAdmin || isNekiAdmin
      ? targetAudiences[0]
      : last(targetAudiences);
  };

  const getDefaultAuthor = () => {
    const selected = getValues('postAs');
    let author = authorTypes.find((a) => a.value === selected);

    if (isP2PFundraiser) {
      setValue('postAs', ProfileTypes.Individual);
      return authorTypes[1];
    }

    return author;
  };

  const [selectedAudience, setSelectedAudience] = useState(
    getDefaultAudience(),
  );
  const [selectedAuthor, setSelectedAuthor] = useState(getDefaultAuthor());
  const [displayOrg, setDisplayOrg] = useState(user?.profile?.activeCommunity);

  const clearSelectedCompanies = () => {
    setValue('targetOrgs', [], {
      shouldValidate: true,
      shouldDirty: true,
    });
    trigger('targetOrgs');
  };

  const clearPostNonProfit = () => {
    setValue('postOrg', null, {
      shouldValidate: true,
      shouldDirty: true,
    });
    trigger('postOrg');
  };

  const handleSetPostOrg = (selected) => {
    setValue('postOrg', selected);
    setDisplayOrg(selected);
    trigger('postOrg');

    setAuthorTypes((prevAuthorTypes) => {
      const updatedAuthorTypes = prevAuthorTypes.map((authorType) =>
        authorType.value === 'org'
          ? { ...authorType, title: selected?.name }
          : authorType,
      );
      return updatedAuthorTypes;
    });
    setTargetAudiences((prevTargetAudiences) => {
      const updatedTargetAudiences = prevTargetAudiences.map(
        (targetAudience) =>
          targetAudience.value === 'my-org'
            ? { ...targetAudience, title: selected?.name }
            : targetAudience,
      );
      return updatedTargetAudiences;
    });
  };

  useEffect(() => {
    const { postAs, targetViewers, nekiAdminAuthor, nekiAdminOrg, postOrg } =
      getValues();

    if (postOrg) {
      setDisplayOrg(postOrg);
      setAuthorTypes((prevAuthorTypes) => {
        const updatedAuthorTypes = prevAuthorTypes.map((authorType) =>
          authorType.value === 'org'
            ? { ...authorType, title: postOrg?.name }
            : authorType,
        );
        return updatedAuthorTypes;
      });
      setTargetAudiences((prevTargetAudiences) => {
        const updatedTargetAudiences = prevTargetAudiences.map(
          (targetAudience) =>
            targetAudience.value === 'my-org'
              ? { ...targetAudience, title: postOrg?.name }
              : targetAudience,
        );
        return updatedTargetAudiences;
      });
    }

    // if (!post && postAs !== selectedProfile) {
    //   setValue('postAs', selectedProfile, {
    //     shouldValidate: true,
    //     shouldDirty: true,
    //   });
    // }

    // if (!post && targetViewers !== selectedAudience.value) {
    //   setValue('targetViewers', selectedAudience.value, {
    //     shouldValidate: true,
    //     shouldDirty: true,
    //   });
    // }

    // setValue('postOrg', userProfile?.activeCommunity, {
    //   shouldValidate: true,
    //   shouldDirty: true,
    // });

    // handlePostOrgSelection(userProfile?.activeCommunity);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectAudience = (audience) => {
    if (isNonProfitAdmin || isNekiAdmin) {
      setSelectedAudience(audience);
      setValue('targetViewers', audience.value, {
        shouldValidate: true,
        shouldDirty: true,
      });

      if (audience.value !== TargetViewers.SelectedOrg) {
        clearSelectedCompanies();
      }
    }
  };

  const handleSelectAuthor = (author) => {
    setSelectedAuthor(author);
    setValue('postAs', author.value, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleTargetOrgSelection = (selected) => {
    setValue(
      'targetOrgs',
      selected.map((s) => s.id),
    );
    trigger('targetOrgs');
  };

  const handlePostOrgSelection = (selected) => {
    handleSetPostOrg(selected);
  };

  useEffect(() => {
    const defaultAuthor = getDefaultAuthor();
    const defaultAudience = getDefaultAudience();
    setValue('nekiAdminAuthor', defaultAuthor);
    setValue('nekiAdminOrg', displayOrg);
    setSelectedAuthor(defaultAuthor);
    setSelectedAudience(defaultAudience);
  }, [handlePostOrgSelection]);

  const renderPostAs = (author) => {
    return (
      <div className="flex flex-col items-start justify-start gap-2 sm:flex-row sm:items-center sm:gap-6">
        <Popover className="relative">
          {({ open }) => (
            <>
              <Popover.Button
                className={cx(
                  'rounded-md px-1 py-2 pl-0',
                  open && 'opacity-80',
                  (isNonProfitAdmin || isNekiAdmin) && 'group',
                )}
                disabled={disabled || !(isNonProfitAdmin || isNekiAdmin)}>
                <div className="flex items-center gap-2">
                  <ProfileAvatar
                    customProfileType={author?.value}
                    customOrg={displayOrg}
                  />
                  <div className="flex flex-col items-start justify-start">
                    <Typography
                      variant="subtitle1"
                      className={cx(
                        'group-hover:text-text-hyperlink',
                        open && 'text-text-hyperlink',
                      )}>
                      {displayOrg?.name}
                    </Typography>
                    <div className="flex items-center justify-start gap-0.5">
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        className={cx(
                          'text-left group-hover:text-text-hyperlink',
                          open && 'text-text-hyperlink',
                        )}>
                        Post as {author.title}
                      </Typography>
                      {!disabled && (
                        <BiCaretDown className="text-text-secondary" />
                      )}
                    </div>
                  </div>
                </div>
              </Popover.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1">
                <Popover.Panel className="absolute left-0 z-10 mt-1 transform px-4 sm:px-0">
                  {({ close }) => (
                    <div className="w-max overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                      <div className="relative grid gap-1 bg-white">
                        {authorTypes.map((item) => (
                          <div
                            key={item.value}
                            role="button"
                            onClick={() => {
                              handleSelectAuthor(item);
                              close();
                            }}
                            className="flex items-center px-3 py-2 transition duration-150 ease-in-out hover:bg-primary-light-1 focus:outline-none focus-visible:ring focus-visible:ring-secondary focus-visible:ring-opacity-50">
                            <div className="flex shrink-0 items-center justify-center text-white">
                              <item.icon
                                aria-hidden="true"
                                className="h-8 w-8 text-secondary opacity-80"
                              />
                            </div>
                            <div className="ml-3">
                              <Typography variant="h5">{item.title}</Typography>
                              <Typography variant="body1" color="textSecondary">
                                {item.subtitle}
                              </Typography>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Popover.Panel>
              </Transition>
            </>
          )}
        </Popover>

        {/* // TODO: check */}
        {/* {author?.value === 'org' && ( */}
        <div className="flex flex-1 items-center gap-2">
          <author.icon className="h-8 w-8 text-text-disabled" />
          <Controller
            control={control}
            name="postOrg"
            render={({ field: { value } }) => (
              <NonProfitSearchBox
                //   multiple
                initialValue={value}
                onChange={(val) => {
                  handlePostOrgSelection(val);
                }}
                error={errors.postOrg?.message}
              />
            )}
          />
        </div>
        {/* )} */}
      </div>
    );
  };

  const renderPostTo = (author, audience) => {
    return (
      <div className="flex flex-col items-start justify-start gap-2 sm:flex-row sm:items-center sm:gap-6">
        <Popover className="relative">
          {({ open }) => (
            <>
              <Popover.Button
                className={cx(
                  'rounded-md px-1 py-2 pl-0',
                  open && 'opacity-80',
                  (isNonProfitAdmin || isNekiAdmin) && 'group',
                )}
                disabled={disabled || !(isNonProfitAdmin || isNekiAdmin)}>
                <div className="flex items-center gap-2">
                  <ProfileAvatar
                    customProfileType={author?.value}
                    customOrg={displayOrg}
                  />
                  <div className="flex flex-col items-start justify-start">
                    <Typography
                      variant="subtitle1"
                      className={cx(
                        'group-hover:text-text-hyperlink',
                        open && 'text-text-hyperlink',
                      )}>
                      {displayOrg?.name}
                    </Typography>
                    <div className="flex items-center justify-start gap-0.5">
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        className={cx(
                          'text-left group-hover:text-text-hyperlink',
                          open && 'text-text-hyperlink',
                        )}>
                        Post to {audience.title}
                      </Typography>
                      {!disabled && (
                        <BiCaretDown className="text-text-secondary" />
                      )}
                    </div>
                  </div>
                </div>
              </Popover.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1">
                <Popover.Panel className="absolute left-0 z-10 mt-1 transform px-4 sm:px-0">
                  {({ close }) => (
                    <div className="w-max overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                      <div className="relative grid gap-1 bg-white">
                        {targetAudiences.map((item) => (
                          <div
                            key={item.value}
                            role="button"
                            onClick={() => {
                              handleSelectAudience(item);
                              close();
                            }}
                            className="flex items-center px-3 py-2 transition duration-150 ease-in-out hover:bg-primary-light-1 focus:outline-none focus-visible:ring focus-visible:ring-secondary focus-visible:ring-opacity-50">
                            <div className="flex shrink-0 items-center justify-center text-white">
                              <item.icon
                                aria-hidden="true"
                                className="h-8 w-8 text-secondary opacity-80"
                              />
                            </div>
                            <div className="ml-3">
                              <Typography variant="h5">{item.title}</Typography>
                              <Typography variant="body1" color="textSecondary">
                                {item.subtitle}
                              </Typography>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Popover.Panel>
              </Transition>
            </>
          )}
        </Popover>

        {audience?.value === 'selected-org' && (
          <div className="flex flex-1 items-center gap-2">
            <audience.icon className="h-8 w-8 text-text-disabled" />
            <Controller
              control={control}
              name="targetOrgs"
              render={({ field: { value } }) => (
                <CompanySearchBox
                  multiple
                  initialValue={value}
                  onChange={(val) => {
                    handleTargetOrgSelection(val);
                  }}
                  error={errors.targetOrgs?.message}
                />
              )}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {renderPostAs(selectedAuthor)}
      {renderPostTo(selectedAuthor, selectedAudience)}
    </>
  );
}

export default PostViewersSelectorNekiAdmin;
